import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useKeycloak} from '@react-keycloak/web';
import {useIntl} from 'react-intl';
import moment from 'moment';

import {
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Select,
} from 'antd';
import SpecialitySelect from 'pages/components/SpecialitySelect';
import PractitionerSelect from 'pages/components/PractitionerSelect';
import LocationSelect from 'pages/components/LocationSelect';
import {RequestContext} from './RequestContext';
import {useHealthInfo} from 'hooks/HealthHooks';

import {createRequest, updateRequest} from 'services/RequestService';
import {handleError} from 'utils/MessageUtils';
import {isEmptyObject} from 'utils/Utils';
import {ServiceRequestStatus} from 'shared/constants/AppConst';

const RequestEditor = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const {patient} = useHealthInfo();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const requestContext = useContext(RequestContext);
  const {selectedRequest, setSelectedRequest, editing, setEditing} =
    requestContext;
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    if (editing) {
      if (
        selectedRequest?.id &&
        (selectedRequest.status === ServiceRequestStatus.completed ||
          selectedRequest.status === ServiceRequestStatus.cancel)
      ) {
        setReadOnly(true);
        form.resetFields();
      }
    }
  }, [editing]);

  const onClose = () => {
    setEditing(false);
  };

  const onReset = () => {
    form.resetFields();
  };

  const onSave = (values) => {
    const data = {
      ...values,
      patient: isEmptyObject(values.patient) ? null : values.patient,
      requester: isEmptyObject(values.requester) ? null : values.requester,
      performer: isEmptyObject(values.performer) ? null : values.performer,
      location: isEmptyObject(values.location) ? null : values.location,
      speciality: isEmptyObject(values.speciality) ? null : values.speciality,
      occurrenceStartTime: values.occurrenceStartTime?.format(
        'DD/MM/YYYY HH:mm:ss',
      ),
      occurrenceEndTime: values.occurrenceEndTime?.format(
        'DD/MM/YYYY HH:mm:ss',
      ),
      performanceStartTime: values.performanceStartTime?.format(
        'DD/MM/YYYY HH:mm:ss',
      ),
      performanceEndTime: values.performanceEndTime?.format(
        'DD/MM/YYYY HH:mm:ss',
      ),
    };

    if (selectedRequest.id) {
      updateRequest(headers, selectedRequest.id, data)
        .then((res) => {
          setSelectedRequest(res.data);
          setEditing(false);
          navigate(`/health/requests/${res.data.id}`);
          message.success(intl.messages['common.updated']);
        })
        .catch((error) => handleError(message, error));
    } else {
      createRequest(headers, data)
        .then((res) => {
          setSelectedRequest(res.data);
          setEditing(false);
          navigate(`/health/requests/${res.data.id}`);
          message.success(intl.messages['common.created']);
        })
        .catch((error) => handleError(message, error));
    }
  };

  return (
    <Drawer
      title={intl.messages['common.detail']}
      width={800}
      onClose={onClose}
      visible={editing}
      destroyOnClose={true}
      bodyStyle={{paddingBottom: 80}}>
      <Form
        layout='vertical'
        form={form}
        onFinish={onSave}
        initialValues={{
          ...selectedRequest,
          patient: {
            id: patient?.id,
          },
          occurrenceStartTime: selectedRequest?.occurrenceStartTime
            ? moment(selectedRequest.occurrenceStartTime, 'DD/MM/YYYY HH:mm')
            : null,
          occurrenceEndTime: selectedRequest?.occurrenceEndTime
            ? moment(selectedRequest.occurrenceEndTime, 'DD/MM/YYYY HH:mm')
            : null,
          performanceStartTime: selectedRequest?.performanceStartTime
            ? moment(selectedRequest.performanceStartTime, 'DD/MM/YYYY HH:mm')
            : null,
          performanceEndTime: selectedRequest?.performanceEndTime
            ? moment(selectedRequest.performanceEndTime, 'DD/MM/YYYY HH:mm')
            : null,
        }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['requester', 'id']}
              label={intl.messages['request.requester']}
              rules={[
                {
                  required: true,
                  message: `${intl.messages['request.requester']} ${intl.messages['validation.required']}`,
                },
              ]}>
              <PractitionerSelect
                disabled={readOnly}
                onChange={(value) => {
                  form.setFieldsValue({requester: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({requester: null});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['performer', 'id']}
              label={intl.messages['request.performer']}>
              <PractitionerSelect
                disabled={readOnly}
                onChange={(value) => {
                  form.setFieldsValue({performer: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({performer: null});
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='occurrenceStartTime'
              label={intl.messages['request.occurrenceStartTime']}>
              <DatePicker
                disabled={readOnly}
                showTime={{
                  format: 'HH:mm',
                }}
                format='DD/MM/YYYY HH:mm'
                style={{width: '100%'}}
                allowClear={true}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='occurrenceEndTime'
              label={intl.messages['request.occurrenceEndTime']}>
              <DatePicker
                disabled={readOnly}
                showTime={{
                  format: 'HH:mm',
                }}
                format='DD/MM/YYYY HH:mm'
                style={{width: '100%'}}
                allowClear={true}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='performanceStartTime'
              label={intl.messages['request.performanceStartTime']}>
              <DatePicker
                disabled={readOnly}
                showTime={{
                  format: 'HH:mm',
                }}
                format='DD/MM/YYYY HH:mm'
                style={{width: '100%'}}
                allowClear={true}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='performanceEndTime'
              label={intl.messages['request.performanceEndTime']}>
              <DatePicker
                disabled={readOnly}
                showTime={{
                  format: 'HH:mm',
                }}
                format='DD/MM/YYYY HH:mm'
                style={{width: '100%'}}
                allowClear={true}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['speciality', 'id']}
              label={intl.messages['request.speciality']}>
              <SpecialitySelect
                disabled={readOnly}
                onChange={(value) => {
                  form.setFieldsValue({speciality: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({speciality: null});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='status'
              label={intl.messages['request.status']}
              rules={[
                {
                  required: true,
                  message: `${intl.messages['request.status']} ${intl.messages['validation.required']}`,
                },
              ]}>
              <Select disabled={readOnly}>
                {(!selectedRequest?.id ||
                  selectedRequest?.status === ServiceRequestStatus.draft) && (
                  <Select.Option value={ServiceRequestStatus.draft}>
                    {intl.messages['request.status.draft']}
                  </Select.Option>
                )}
                {(selectedRequest?.status === ServiceRequestStatus.draft ||
                  selectedRequest?.status === ServiceRequestStatus.active) && (
                  <>
                    <Select.Option value={ServiceRequestStatus.active}>
                      {intl.messages['request.status.active']}
                    </Select.Option>
                    <Select.Option value={ServiceRequestStatus.cancel}>
                      {intl.messages['request.status.cancel']}
                    </Select.Option>
                  </>
                )}
                {selectedRequest?.status === ServiceRequestStatus.cancel && (
                  <Select.Option value={ServiceRequestStatus.cancel}>
                    {intl.messages['request.status.cancel']}
                  </Select.Option>
                )}
                {(selectedRequest?.status === ServiceRequestStatus.active ||
                  selectedRequest?.status ===
                    ServiceRequestStatus.completed) && (
                  <Select.Option value={ServiceRequestStatus.completed}>
                    {intl.messages['request.status.completed']}
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name='symptom' label={intl.messages['request.symptom']}>
              <Input.TextArea rows={5} disabled={readOnly} maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name='instruction'
              label={intl.messages['request.instruction']}>
              <Input.TextArea rows={5} disabled={readOnly} maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name={['location', 'id']}
              label={intl.messages['request.location']}>
              <LocationSelect
                disabled={readOnly}
                onChange={(value) => {
                  form.setFieldsValue({location: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({location: null});
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        {!readOnly && (
          <Row>
            <Col span={9} />
            <Form.Item shouldUpdate noStyle>
              <Col span={3}>
                <Button onClick={onReset} style={{width: '90px'}}>
                  {intl.messages['common.reset']}
                </Button>
              </Col>
              <Col span={3}>
                <Button
                  type='primary'
                  htmlType='submit'
                  style={{width: '90px'}}>
                  {intl.messages['common.save']}
                </Button>
              </Col>
            </Form.Item>
            <Col span={9} />
          </Row>
        )}
      </Form>
    </Drawer>
  );
};

export default RequestEditor;
